import {useQuery} from '@tanstack/react-query'
import {sanityClient} from '@/lib/sanity'
import {POST_BY_SLUG_QUERY} from '@/lib/sanityQueries'
import type {BlogPost} from '@/types/blog'

interface UseBlogPostOptions {
  preview?: boolean
  previewSecret?: string
}

const fetchPublished = async (slug: string): Promise<BlogPost | null> => {
  const result = await sanityClient.fetch<BlogPost | null>(POST_BY_SLUG_QUERY, {slug})
  return result
}

const fetchPreview = async (slug: string, secret: string): Promise<BlogPost | null> => {
  const params = new URLSearchParams({secret})
  const url = `/api/blog/preview/${encodeURIComponent(slug)}?${params.toString()}`
  const response = await fetch(url, {credentials: 'same-origin'})
  if (response.status === 404) return null
  if (!response.ok) {
    throw new Error(`Preview fetch failed (${response.status})`)
  }
  const data = (await response.json()) as {post: BlogPost | null}
  return data.post
}

export const useBlogPost = (slug: string | undefined, options: UseBlogPostOptions = {}) => {
  const preview = options.preview ?? false
  const previewSecret = options.previewSecret ?? ''
  const previewActive = preview && previewSecret.length > 0
  return useQuery({
    queryKey: ['blog', 'post', slug, {preview: previewActive}],
    queryFn: () => {
      if (!slug) return Promise.resolve<BlogPost | null>(null)
      return previewActive ? fetchPreview(slug, previewSecret) : fetchPublished(slug)
    },
    enabled: Boolean(slug),
    staleTime: previewActive ? 0 : 1000 * 60,
  })
}
